import type { LayerInspectorSelection } from "../../../helpers/layerInspectorTools";

export type TimelineMarqueeSelectionResult =
  | { type: "keyframes"; keyframeIds: string[] }
  | { type: "layers"; layerSelections: LayerInspectorSelection[] };

export function rectsIntersect(a: DOMRect, b: DOMRect): boolean {
  return (
    a.left <= b.right &&
    a.right >= b.left &&
    a.top <= b.bottom &&
    a.bottom >= b.top
  );
}

function getIntersectingKeyframeIds(
  container: HTMLElement,
  selectionRect: DOMRect
): string[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(".timeline-keyframe-marker")
  )
    .filter((element) =>
      rectsIntersect(selectionRect, element.getBoundingClientRect())
    )
    .map((element) => element.dataset.keyframeId)
    .filter((id): id is string => Boolean(id));
}

function getIntersectingTrackBarSelections(
  container: HTMLElement,
  selectionRect: DOMRect,
  layerSelectionsByNodeKey: Map<string, LayerInspectorSelection>
): LayerInspectorSelection[] {
  const selections: LayerInspectorSelection[] = [];
  const seenNodeKeys = new Set<string>();

  container
    .querySelectorAll<HTMLElement>(".timeline-track-bar[data-node-key]")
    .forEach((element) => {
      if (!rectsIntersect(selectionRect, element.getBoundingClientRect())) {
        return;
      }

      const nodeKey = element.dataset.nodeKey;
      if (!nodeKey || seenNodeKeys.has(nodeKey)) {
        return;
      }

      const selection = layerSelectionsByNodeKey.get(nodeKey);
      if (!selection) {
        return;
      }

      seenNodeKeys.add(nodeKey);
      selections.push(selection);
    });

  return selections;
}

export function getTimelineMarqueeSelection(params: {
  container: HTMLElement;
  selectionRect: DOMRect;
  layerSelectionsByNodeKey: Map<string, LayerInspectorSelection>;
}): TimelineMarqueeSelectionResult {
  const { container, selectionRect, layerSelectionsByNodeKey } = params;
  const keyframeIds = getIntersectingKeyframeIds(container, selectionRect);

  if (keyframeIds.length > 0) {
    return { type: "keyframes", keyframeIds };
  }

  return {
    type: "layers",
    layerSelections: getIntersectingTrackBarSelections(
      container,
      selectionRect,
      layerSelectionsByNodeKey
    ),
  };
}
